import type {
  PermissionAction,
  PermissionGroupId,
  ProfilePermissions,
} from '@strength/domain';
import { isPermissionGranted, permissionValue } from '@strength/domain';
import { queries } from '@strength/zero-schema';
import { useZeroQuery } from '../../zero/useZeroQuery';
import { useIsZeroActive } from './zero-active';

export type ZeroPermissionsStatus = 'inactive' | 'loading' | 'error' | 'ready';

export type ZeroPermissions = {
  status: ZeroPermissionsStatus;
  /**
   * 'ready' only: the stored document — `{}` when no ProfilePermission row is
   * stored, the same shape the server guard reads (getCachedRole's
   * `permissions: null`; Rule 3 — absent keys grant, only explicit false
   * denies). Null in every other status.
   */
  permissions: ProfilePermissions | null;
};

const INACTIVE: ZeroPermissions = { status: 'inactive', permissions: null };

/** Rule 3 read of a resolved document. */
export function isZeroPermissionGranted(
  permissions: ProfilePermissions | null,
  group: PermissionGroupId,
  action: PermissionAction,
): boolean {
  return isPermissionGranted(permissionValue(permissions ?? {}, group, action));
}

/**
 * SC-5685: subscribes to the viewer's own ProfilePermission row through Zero
 * (`viewer.profilePermission`) so permission gates can re-evaluate live
 * through IVM — a mid-session revocation hides data without a reload. The
 * subscription is self-scoped, so the replica materializes exactly the
 * viewer's one permission row, never the org-wide matrix.
 *
 * Status mirrors the viewer-document resolution discipline (SC-4768):
 * 'loading' while the subscription has not resolved, 'error' when it failed
 * (permission-driven callers fail closed), 'ready' once the row synced, and
 * 'inactive' when Zero is not active (callers fall back to the tRPC-resolved
 * viewer document).
 */
export function useZeroPermissions(): ZeroPermissions {
  const isZeroActive = useIsZeroActive();
  const [rows, details] = useZeroQuery(
    isZeroActive ? queries.viewer.profilePermission() : undefined,
  );

  if (!isZeroActive) {
    return INACTIVE;
  }
  if (details.type === 'error') {
    return { status: 'error', permissions: null };
  }
  if (details.type !== 'complete' || rows === undefined) {
    return { status: 'loading', permissions: null };
  }
  // SAFETY: the definition's root table is ProfilePermission; the normalized
  // row's `permissions` json column is the stored ProfilePermissions doc.
  const row = rows[0] as { permissions?: ProfilePermissions } | undefined;
  return {
    status: 'ready',
    permissions: row?.permissions ?? {},
  };
}
